import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Lottie from "react-lottie-player";
import Animation from "../Login/assets/SelectAnimation.json";
import { ContextSession } from "../../services/ContextSession";

const SHOW_SELECTION_ANIMATION = false;

const DialogComponent = () => {
  let sessi = useContext(ContextSession);
  const [open, setOpen] = useState(true);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState("");
  const [alertMessages, setAlertMessages] = useState([]);
  const navigate = useNavigate();
  const estadoParticipante = sessi.sessionData.estado || "";
  const isJubiladoOPensionado = [
    "JUBILADO",
    "Jubilado",
    "jubilado",
    "PENSIONADO",
    "Pensionado",
    "pensionado",
  ].includes(estadoParticipante);
  const isActivo = ["ACTIVO", "Activo", "activo"].includes(
    estadoParticipante
  );

  useEffect(() => {
    // Fetch options from API
    axios
      .get(`${sessi.ip}/api/TiposSolicitudes/`)
      //.get(`http://172.20.7.10:8082/api/TiposSolicitudes/`)
      .then((response) => {
        setOptions(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching options:", error);
        setLoading(false);
      });
  }, []);

  const filteredOptions = useMemo(() => {
    return options.filter((option) => {
      if (
        [
          "Constancia para Pensionados",
          "Constancia para Jubilados y Pensionados",
          "Consulta de Revalorización de Pensión",
        ].includes(option.TipoSolicitud)
      ) {
        return isJubiladoOPensionado;
      }

      if (option.TipoSolicitud === "Visualizar Saldo de Cotizaciones") {
        return isActivo;
      }

      return true;
    });
  }, [options, isActivo, isJubiladoOPensionado]);

  useEffect(() => {
    if (!selectedOption) {
      return;
    }

    const selectedStillVisible = filteredOptions.some(
      (option) => option.tiposolicitudid === parseInt(selectedOption)
    );

    if (!selectedStillVisible) {
      setSelectedOption("");
    }
  }, [filteredOptions, selectedOption]);

  const handleClose = async () => {
    if (selectedOption) {
      const selectedText = filteredOptions.find(
        (option) => option.tiposolicitudid === parseInt(selectedOption)
      )?.TipoSolicitud;
      let messages = [];

      if (selectedText === "Solicitud de Preanálisis de Prestamos") {
        try {
          const response = await axios.get(
            `${sessi.ip}/solicitudesMes/${sessi.sessionData.id}/`,
            {
              //const response = await axios.get(`http://172.20.7.10:8082//solicitudesMes/${sessi.sessionData.id}/`, {
              headers: {
                Authorization: `Bearer ${sessi.sessionData.token}`, // Se añade el token aquí
              },
            }
          );
          const { cantSolicitudesMes, cantSolicitudActiva } = response.data;

          /*// Check if the monthly request limit has been reached
          if (cantSolicitudesMes >= 3) {
            messages.push('Se ha alcanzado la cifra máxima (3) de solicitudes de preanálisis realizadas en el mes en curso. Podrá realizar una nueva solicitud el día 1 del próximo mes.');
          }*/

          // Check if there is an active request
          if (cantSolicitudActiva >= 1) {
            messages.push(
              "Hay una solicitud de preanálisis activa. Debe esperar el cierre de la misma para poder realizar otra solicitud."
            );
          }

          // Navigate to the form if no messages
          if (messages.length === 0) {
            setOpen(false); // Close the dialog
            navigate("/Mis Solicitudes/Formulario");
            return;
          }
        } catch (error) {
          console.error("Error fetching solicitud data:", error);
          messages.push(
            "Hubo un problema al consultar la información. Cierre la sesión y vuelva a ingresar al sistema para intentar nuevamente."
          );
        }
      } else if (selectedText === "Queja y Sugerencias") {
        setOpen(false); // Close the dialog
        navigate("/Mis Solicitudes/QuejasSugerencias");
      } else if (selectedText === "Constancia para Pensionados") {
        if (
          sessi.sessionData.estado === "JUBILADO" ||
          sessi.sessionData.estado === "Jubilado" ||
          sessi.sessionData.estado === "jubilado" ||
          sessi.sessionData.estado === "PENSIONADO" ||
          sessi.sessionData.estado === "Pensionado" ||
          sessi.sessionData.estado === "pensionado"
        ) {
          setOpen(false); // Close the dialog
          navigate("/Mis Solicitudes/ConstanciaJubilacion");
        } else {
          messages.push(
            "La solicitud de esta constancia es solo para Jubilados y Pensionados."
          );
        }
      } else if (selectedText === "Constancia Razonada") {
        setOpen(false); // Close the dialog
        navigate("/Mis Solicitudes/ConstanciaRazonada");
      } else if (selectedText === "Constancia Historial de Cotizaciones") {
        setOpen(false); // Close the dialog
        navigate("/Mis Solicitudes/ConstanciaHistorialCotizaciones");
      } else if (
        selectedText === "Constancia para el Pago de Prestaciones" ||
        selectedText === "Constancia de Prestaciones"
      ) {
        setOpen(false); // Close the dialog
        navigate("/Mis Solicitudes/SolicitudConstanciaPrestaciones");
      }

      setAlertMessages(messages);
    } else {
      setAlertMessages(["Por favor, seleccione una opción."]);
    }
  };

  const handleChange = (event) => {
    setSelectedOption(event.target.value);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        navigate("/Dashboard");
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          display: "flex",
          flexDirection: "column",
          position: "relative",
        },
      }}
    >
      <DialogTitle>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          position="relative"
        >
          {SHOW_SELECTION_ANIMATION && (
            <Lottie
              loop
              animationData={Animation}
              play
              style={{ width: "50%", height: "auto" }}
            />
          )}
          <span>Seleccione el tipo de solicitud</span>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => navigate("/Dashboard")}
            aria-label="close"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundcolor: "var(--color-brand-secondary)",
              color: "white",
              "&:hover": {
                backgroundColor: "darkred",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
            <FormControl fullWidth sx={{ maxWidth: 400 }}>
              <InputLabel>Tipo de Solicitud</InputLabel>
              <Select
                value={selectedOption}
                onChange={handleChange}
                label="Tipo de Solicitud"
                sx={{ width: "100%" }}
              >
                {filteredOptions.map((option) => (
                  <MenuItem
                    key={option.tiposolicitudid}
                    value={option.tiposolicitudid}
                  >
                    {option.TipoSolicitud}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClose}
              sx={{ mt: 2 }}
            >
              Realizar Solicitud
            </Button>
            {alertMessages.map((msg, index) => (
              <Box mt={2} key={index}>
                <Alert severity="warning" sx={{ textAlign: "justify" }}>
                  {msg}
                </Alert>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DialogComponent;
